import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { MeterReaderZoneType } from "@/types/meter"
import { deleteMeterReader } from "@/lib/api/meter"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import MeterReaderForm from "@/components/forms/meter-reader-form"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"

interface DataProps {
  data: MeterReaderZoneType
}

export function EditMeterReaderOperations({ data }: DataProps) {
  const [showDropDown, setShowDropDown] = React.useState<boolean>(false)
  const [showMeterReaderModal, setShowMeterReaderModal] =
    React.useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)

  return (
    <div>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setShowDropDown(false)
              setShowMeterReaderModal(!showDeleteModal)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Meter Reader
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => {
              setShowDropDown(false)
              setShowDeleteModal(!showDeleteModal)
            }}
          >
            <Icons.delete className="mr-2 size-4" />
            Delete Meter Reader
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteMeterSizeModal
        meterReaderId={data.id}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
      />

      <EditMeterReaderModal
        editData={data}
        showMeterReaderModal={showMeterReaderModal}
        setShowMeterReaderModal={setShowMeterReaderModal}
      />
    </div>
  )
}

function EditMeterReaderModal({
  editData,
  showMeterReaderModal,
  setShowMeterReaderModal,
}: {
  editData: MeterReaderZoneType
  showMeterReaderModal: boolean
  setShowMeterReaderModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  return (
    <Credenza
      open={showMeterReaderModal}
      onOpenChange={setShowMeterReaderModal}
    >
      <CredenzaContent className="w-full md:min-w-[600px]">
        <CredenzaTitle>
          <Header>Edit Meter Reader</Header>
        </CredenzaTitle>
        <CredenzaBody>
          <MeterReaderForm
            setShowMeterReaderModal={setShowMeterReaderModal}
            editData={editData}
          />
        </CredenzaBody>
      </CredenzaContent>
    </Credenza>
  )
}

interface DeleteMeterSizeModalProps {
  meterReaderId: number
  showDeleteModal: boolean
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>
}

function DeleteMeterSizeModal({
  meterReaderId,
  showDeleteModal,
  setShowDeleteModal,
}: DeleteMeterSizeModalProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const formSchema = z.object({
    id: z.coerce
      .number()
      .min(1, "Enter the id below to delete")
      .refine((val) => val === meterReaderId, {
        message: "Input ID type must match the Meter Reader ID.",
      }),
  })

  const form = useForm({
    resolver: zodResolver(formSchema),
  })
  const mutation = useMutation({
    mutationFn: async () => {
      await deleteMeterReader(meterReaderId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meterReaderDetails"] })
      toast({
        description: "Meter reader deleted successfully.",
      })
      setShowDeleteModal(false)
    },
  })

  function handleDelete() {
    mutation.mutate()
  }

  return (
    <Credenza open={showDeleteModal} onOpenChange={setShowDeleteModal}>
      <CredenzaContent>
        <Form {...form}>
          <form
            className="space-y-4 pb-4"
            onSubmit={form.handleSubmit(handleDelete)}
          >
            <CredenzaHeader>
              <CredenzaTitle>Delete Meter Size</CredenzaTitle>
              <CredenzaDescription>
                You cannot undo this action!
              </CredenzaDescription>
            </CredenzaHeader>
            <CredenzaBody>
              <FormField
                control={form.control}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type "{meterReaderId}" to delete.</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""} // Allow empty input
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? undefined
                              : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CredenzaBody>
            <CredenzaFooter>
              <CredenzaClose asChild>
                <Button variant={"outline"}>Cancel</Button>
              </CredenzaClose>
              <Button
                disabled={mutation.isPending}
                type="submit"
                variant={"destructive"}
              >
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Delete
              </Button>
            </CredenzaFooter>
          </form>
        </Form>
      </CredenzaContent>
    </Credenza>
  )
}
